/* ═══════════════════════════════════════════════════════════════════════════
   GFI IndexedDB Storage Utility
   Stores documents (PDFs, images) in IndexedDB instead of localStorage
   to avoid the ~5MB quota limit. IndexedDB supports ~50-100MB+.
   ═══════════════════════════════════════════════════════════════════════════ */

const DB_NAME = "gfi-nexus-db";
/* 2026-10-21 — real error caught live in Arturo's browser console:
   "NotFoundError: Failed to execute 'transaction' on 'IDBDatabase':
   One of the specified object stores was not found." His browser had
   a `gfi-nexus-db` database already sitting at version 1 WITHOUT the
   "documents" object store (likely created/left in that state by some
   earlier partial/interrupted run of this code, before this store
   existed or before onupgradeneeded ever got to create it) —
   `indexedDB.open(DB_NAME, 1)` against an already-version-1 database
   never fires `onupgradeneeded` again, so that missing store could
   never self-heal; every saveDoc/loadDoc against it threw this exact
   error. Bumping the requested version forces `onupgradeneeded` to run
   once more for EVERY browser whose local DB is still below it — the
   handler already checks `objectStoreNames.contains(STORE_NAME)`
   before creating it, so this is a no-op (and loses no data) for any
   browser that already has the store correctly, and a real repair for
   any browser stuck like Arturo's. */
const DB_VERSION = 2;
const STORE_NAME = "documents";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Save documents for a given key (e.g. "cert-docs-123", "cert-gs-456")
 * @param {string} key - unique storage key
 * @param {any} data - data to store (arrays, objects with base64, etc.)
 */
/* 2026-10-21 — real bug found live (Arturo: a bill's SupportingDocsButton
   panel stuck on "Cargando…" forever, and the 3 files it had never made
   it into the Sales Report either). Root cause: these 3 functions used
   to `return new Promise(...)` directly from inside the try block —
   that hands the pending Promise back to the caller immediately, so
   when the transaction's onerror/onsuccess fires LATER (after the try
   block has already exited), a rejection is never caught by the local
   catch — it becomes an unhandled rejection the caller's own .then()
   silently never resolves from (exactly "stuck loading forever"), and
   any caller's own try/catch around an `await saveDoc(...)`/`loadDoc(...)`
   call does catch it, but only by skipping the whole operation — which
   is why the files silently never got merged into the PDF. Fixed by
   `await`-ing the inner Promise INSIDE the try, so a rejection is a
   real, local throw the catch block actually sees — these functions
   now always resolve as documented (true/false, or null), never reject. */
export async function saveDoc(key, data) {
  try {
    const db = await openDB();
    const ok = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put(data, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
    return ok;
  } catch (err) {
    console.error("[GFI-IDB] saveDoc error:", key, err);
    return false;
  }
}

/**
 * Load documents for a given key
 * @param {string} key - unique storage key
 * @returns {any} stored data or null
 */
export async function loadDoc(key) {
  try {
    const db = await openDB();
    const result = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    return result;
  } catch (err) {
    console.error("[GFI-IDB] loadDoc error:", key, err);
    return null;
  }
}

/**
 * Delete documents for a given key
 * @param {string} key - unique storage key
 */
export async function deleteDoc(key) {
  try {
    const db = await openDB();
    const ok = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
    return ok;
  } catch (err) {
    console.error("[GFI-IDB] deleteDoc error:", key, err);
    return false;
  }
}

/**
 * Save a map of documents (e.g. { certId: [docs], certId2: [docs] })
 * @param {string} mapKey - e.g. "cert-docs" or "cert-gs"
 * @param {Object} map - { id: data, id2: data }
 */
export async function saveDocMap(mapKey, map) {
  return saveDoc(mapKey, map);
}

/**
 * Load a map of documents
 * @param {string} mapKey
 * @returns {Object} map or empty object
 */
export async function loadDocMap(mapKey) {
  const data = await loadDoc(mapKey);
  return data || {};
}

/**
 * Get approximate IndexedDB usage for diagnostics
 */
export async function getStorageEstimate() {
  if (navigator.storage && navigator.storage.estimate) {
    const est = await navigator.storage.estimate();
    return {
      usage: (est.usage / 1024 / 1024).toFixed(2) + " MB",
      quota: (est.quota / 1024 / 1024).toFixed(0) + " MB",
      percent: ((est.usage / est.quota) * 100).toFixed(1) + "%",
    };
  }
  return { usage: "unknown", quota: "unknown", percent: "unknown" };
}